import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/lib/auth";
import * as ella from "@/lib/ellaApi";
import { toast } from "sonner";

/**
 * Blocking Terms & Conditions consent dialog, shown on the home page for
 * logged-in customers who have not accepted the active T&C version yet.
 * Mirrors the legacy Onsen `termsConsentDialog` flow:
 *   home load → getTermsAcceptanceStatus → TermsRequired?
 *     → modal (title, "Versi X", content, privacy-policy link, consent checkbox)
 *     → "Setuju" (requires checkbox) → doAcceptTermsCondition
 *     → "Keluar" → logout
 */
export default function TermsGate() {
  const { token, logout } = useAuth();
  const { pathname } = useLocation();
  const [terms, setTerms] = useState(null);
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token || pathname !== "/") return;
    let alive = true;
    ella.getTermsStatus().then((res) => {
      if (!alive) return;
      if (res.status && res.data?.TermsRequired && res.data?.Terms) {
        setTerms(res.data.Terms);
        setChecked(false);
        setOpen(true);
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, [token, pathname]);

  const accept = async () => {
    if (!checked) {
      toast.error("Mohon membaca dan menyetujui Kebijakan Privasi Ellafroze terlebih dahulu");
      return;
    }
    setBusy(true);
    try {
      const res = await ella.doAcceptTerms();
      if (res.status) {
        toast.success(res.message || "Persetujuan berhasil disimpan");
        setOpen(false);
        setTerms(null);
      } else {
        toast.error(res.message || "Gagal menyimpan persetujuan");
      }
    } finally { setBusy(false); }
  };

  const exit = async () => {
    setBusy(true);
    try { await logout(); } finally {
      setBusy(false);
      setOpen(false);
      setTerms(null);
      toast.info("Kamu keluar dari akun");
    }
  };

  if (!terms) return null;

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-md sm:rounded-2xl [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="terms-consent-dialog"
      >
        <DialogTitle className="font-display text-lg" data-testid="terms-title">
          {terms.Title || "Kebijakan Privasi Ellafroze"}
        </DialogTitle>
        {terms.Version && (
          <p className="text-xs text-gray-500 -mt-2" data-testid="terms-version">Versi {terms.Version}</p>
        )}
        <div
          className="max-h-[40vh] overflow-y-auto rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700 whitespace-pre-line leading-relaxed"
          data-testid="terms-body"
        >
          {terms.Content}
        </div>
        <p className="text-xs">
          {/* new tab so the consent dialog stays open, like the legacy app */}
          <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-[#D32F2F] underline">
            Baca Kebijakan Privasi lengkap
          </a>
        </p>
        <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
          <Checkbox
            checked={checked}
            onCheckedChange={(v) => setChecked(!!v)}
            data-testid="terms-consent-checkbox"
            className="mt-0.5"
          />
          <span>Saya telah membaca dan menyetujui Kebijakan Privasi Ellafroze yang berlaku.</span>
        </label>
        <div className="flex gap-2 mt-1">
          <Button
            variant="outline"
            onClick={exit}
            disabled={busy}
            className="flex-1 h-11 rounded-full text-[#D32F2F] border-red-200 hover:bg-red-50"
            data-testid="terms-logout-button"
          >
            Keluar
          </Button>
          <Button
            onClick={accept}
            disabled={busy}
            className="flex-1 h-11 btn-primary"
            data-testid="terms-accept-button"
          >
            {busy ? "Menyimpan..." : "Setuju"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
